/*******************************************************************************
 * Copyright     : Copyright (c)  2025 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React from "react";

import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertIcon, ArrowLeftIcon, Badge, Breadcrumbs, Button, InfoIcon, PlusIcon, ProgressIcon, SuccessIcon } from "@forge/common";

import { displayStatus } from "../../utils/utility";
import { useNavigateToLandingPage } from "../../utils/navigationUtils";
import { GatewayStatus, LOCALE_CONST, ROUTE_PATH } from "../../utils/constants";

import { RootState } from "../../store";

import './index.scss';

const CustomPageHeader = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const rLocation = useLocation();

	const { navigateToLandingPage } = useNavigateToLandingPage();

	// Helper function to get tag display text
	const getTagDisplayText = (tag: string) => {
		if (!tag) return null;
		
		// Don't display badge for forgeedge (both raw API value and formatted text)
		if (tag?.toLowerCase() === 'forgeedge' || tag?.toLowerCase() === 'forge edge') return null;
		
		switch (tag?.toLowerCase()) {
			case 'legacy':
				return t(LOCALE_CONST.legacy);
			case 'forgelegacy':
				return t(LOCALE_CONST.forgelegacy);
			case 'azure':
				return t(LOCALE_CONST.fc); // "FC" as per requirement
			case 'fc':
				return t(LOCALE_CONST.fc);
			default:
				return tag; // Return the tag as-is since API provides formatted text
		}
	};

	const { gwAPI, selectedOrg, headerDetails } = useSelector((state: RootState) => {
		return {
			selectedOrg: state.global.selectedOrg,
			headerDetails: state.global.headerDetails,
			gwAPI: state.gateWayManagementAPI?.queries
		};
	});

	// Get the current path from the location object
	const currentPath = rLocation.pathname;

	const renderBackBtn = () => {
		if (currentPath !== "/") {
			return (
				<Button
					icon={<ArrowLeftIcon strokeWidth={2} />}
					iconPosition="left"
					size="large"
					variant="secondary"
					onClick={navigateToLandingPage}
					aria-label={"<-"}
					className="iot-back-button-cls"
				/>
			);
		} else {
			return null;
		}
	};

	const renderBreadCrumb = () => {
		let nextStepLabel = headerDetails?.title || "";

		if (headerDetails?.type === 'create') {
			nextStepLabel = t(LOCALE_CONST.createNewGateway);
		}

		return (
			<div>
				<Breadcrumbs
					crumbs={
						[{
							id: "iot_devices",
							label: t(LOCALE_CONST.gatewayManagement)
						}, {
							id: "new_device",
							label: nextStepLabel
						}]
					}
					separator="/"
					label="Breadcrumbs"
					className="iot-breadcrumbs-cls"
					onChange={navigateToLandingPage}
				/>
			</div>
		);
	};

	const getStatusColor = (status: string) => {
		if ((status === GatewayStatus.Error) || (status === GatewayStatus.CertificateExpired)) {
			return "red";
		} else if (status === GatewayStatus.Pending) {
			return "blue";
		} else if (status === GatewayStatus.Active) {
			return "green";
		} else {
			return "gray";
		}
	};

	const getStatusIcon = (status: string) => {
		const color = getStatusColor(status);
		const iconProps = { strokeWidth: 2, color };

		if ((status === GatewayStatus.Error) || (status === GatewayStatus.CertificateExpired)) {
			return <AlertIcon {...iconProps} />;
		} else if (status === GatewayStatus.Pending) {
			return <ProgressIcon {...iconProps} />;
		} else if (status === GatewayStatus.Active) {
			return <SuccessIcon {...iconProps} />;
		} else if (status === GatewayStatus.Deactivated) {
			return <InfoIcon {...iconProps} />;
		} else {
			return undefined;
		}
	};

	const renderStatusInfo = () => {
		if (headerDetails?.type === 'view' || headerDetails?.type === 'edit') {
			return (
				<div className="view-dev-status-badge-cont-cls">
					<Badge className="custom-badge ml-2"
						icon={getStatusIcon(headerDetails?.status)}
						label="Badge" outline
						color={getStatusColor(headerDetails?.status)}
					>
						<span style={{ color: getStatusColor(headerDetails?.status) }}>
							{t(displayStatus(headerDetails?.status || "-"))}
						</span>
					</Badge>
				</div>
			);
		} else {
			return null;
		}
	};

	const renderCreateBtn = () => {
		if (currentPath === "/") {
			let createBtnDisabled = true;

			if (selectedOrg) {
				Object.keys(gwAPI).forEach((key) => {
					if (gwAPI[key]?.endpointName === "getDeviceList") {
						if (gwAPI[key]?.status === "fulfilled") {
							// If the API call is fulfilled, enable the button
							createBtnDisabled = false;
						} else if (gwAPI[key]?.status === "rejected") {
							const error = gwAPI[key]?.error as { status?: number };
							// If the error status is 404, enable the button
							if (error && error.status === 404) {
								createBtnDisabled = false;
							}
						} else {
							//do nothing
						}
					}
				});
			}

			return (
				<Button
					icon={<PlusIcon />}
					aria-label="Create"
					disabled={createBtnDisabled}
					className="iot-create-device-button-cls"
					onClick={() => navigate(ROUTE_PATH.createDevice)}
				>
					{t(LOCALE_CONST.create)}
				</Button>
			);
		} else {
			return null;
		}
	};

	return (
		<div className="iot-upper-dashboard-page-header-cls">
			{renderBackBtn()}
			<div className="title-area">
				<div>
					{(currentPath !== "/") && renderBreadCrumb()}
					<h1 className="title-text">
						{headerDetails?.title || t(LOCALE_CONST.gatewayManagement)}
						{headerDetails?.tag && getTagDisplayText(headerDetails.tag) && (
							<Badge className="ml-2" color="gray">
								{getTagDisplayText(headerDetails.tag)}
							</Badge>
						)}
					</h1>
				</div>
				{renderCreateBtn()}
			</div>
			{renderStatusInfo()}
		</div>
	);
};

export default CustomPageHeader;